import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Form, Spinner } from 'react-bootstrap';
import axios from '../axios';

// The backend sends commercialUnitPrice at full precision on purpose, so the invoice's
// line/grand totals reconcile with the other commercial documents. That can read as
// 1.155646691097219 in an input box, so trim it for display here — display only. The exact
// value is kept alongside it and written back on save unless the field is actually edited.
const DISPLAY_DECIMALS = 6;

const round_for_display = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (!Number.isFinite(num)) return '';
    // Number() again drops trailing zeros: 1.41312, not 1.413120
    return String(Number(num.toFixed(DISPLAY_DECIMALS)));
};

const EditDisplayModal = ({ show, onHide, commercialId }) => {
    const [table_info, set_table_info] = useState([]);
    const [loading, set_loading] = useState(false);
    const [saving, set_saving] = useState(false);
    const [edits, set_edits] = useState({}); // { [orderGsmId]: { display, qty, unitPrice } }

    useEffect(() => {
        if (show && commercialId) {
            fetch_table_info();
        }
        if (!show) {
            set_table_info([]);
            set_edits({});
        }
    }, [show, commercialId]);

    const fetch_table_info = async () => {
        set_loading(true);
        try {
            const tableResponse = await axios.get(`${window.backendUrl}/get_commercial_pdf_table_info/${commercialId}`);
            const data = tableResponse.data;
            set_table_info(data);

            const initial_edits = {};
            data.forEach((order) => {
                order.items.forEach((item) => {
                    item.specifications.forEach((spec) => {
                        spec.gsms.forEach((gsm) => {
                            initial_edits[gsm.orderGsmId] = {
                                display: !!gsm.commercialMeasurement,
                                qty: gsm.commercialQty ?? '',
                                unitPrice: round_for_display(gsm.commercialUnitPrice),
                                // Exact stored value, plus the trimmed string we actually put in
                                // the input, so handle_save can tell "untouched" from "edited".
                                exactUnitPrice: gsm.commercialUnitPrice ?? '',
                                shownUnitPrice: round_for_display(gsm.commercialUnitPrice)
                            };
                        });
                    });
                });
            });
            set_edits(initial_edits);
        } catch (error) {
            console.error('Error fetching commercial table info:', error);
        } finally {
            set_loading(false);
        }
    };

    const handle_checkbox_change = (orderGsmId) => {
        set_edits((prev) => ({
            ...prev,
            [orderGsmId]: { ...prev[orderGsmId], display: !prev[orderGsmId]?.display }
        }));
    };

    const handle_field_change = (orderGsmId, field, value) => {
        set_edits((prev) => ({
            ...prev,
            [orderGsmId]: { ...prev[orderGsmId], [field]: value }
        }));
    };

    const handle_toggle_all = () => {
        const all_checked = Object.values(edits).every((e) => e.display);
        set_edits((prev) => {
            const updated = {};
            Object.keys(prev).forEach((id) => {
                updated[id] = { ...prev[id], display: !all_checked };
            });
            return updated;
        });
    };

    const handle_save = async () => {
        set_saving(true);
        try {
            const gsms = Object.entries(edits).map(([orderGsmId, values]) => {
                // The input only ever held a trimmed value, so opening this modal and saving
                // without touching the price must not write the trimmed number back — that
                // would quietly round the price and shift the invoice total. Send the exact
                // stored value unless the field was actually edited.
                const price_untouched = String(values.unitPrice) === String(values.shownUnitPrice);
                const price_to_save = price_untouched ? values.exactUnitPrice : values.unitPrice;

                return {
                    orderGsmId: Number(orderGsmId),
                    commercialMeasurement: !!values.display,
                    commercialQty: values.qty === '' || values.qty === null || values.qty === undefined ? 0 : Number(values.qty),
                    commercialUnitPrice: price_to_save === '' || price_to_save === null || price_to_save === undefined ? 0 : Number(price_to_save)
                };
            });

            await axios.put(`${window.backendUrl}/update_commercial_display/${commercialId}`, { gsms });
            onHide();
        } catch (error) {
            console.error('Error saving commercial display:', error);
        } finally {
            set_saving(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>Edit display of commercial</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <Table bordered style={{ fontSize: '13px' }}>
                        <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                            <tr>
                                <th style={{ textAlign: 'center' }}>Style</th>
                                <th style={{ textAlign: 'center' }}>Buyer Name</th>
                                <th style={{ textAlign: 'center' }}>Item Name</th>
                                <th style={{ textAlign: 'center' }}>PLY</th>
                                <th style={{ textAlign: 'center' }}>Measurement</th>
                                <th style={{ textAlign: 'center' }}>Qty</th>
                                {/* <th style={{ textAlign: 'center' }}>Display</th> */}
                                <th style={{ textAlign: 'center' }}>
                                    Display
                                    <br />
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        style={{ fontSize: '11px', padding: '2px 6px', marginTop: '4px' }}
                                        onClick={handle_toggle_all}
                                        disabled={loading}
                                    >
                                        {Object.values(edits).length > 0 && Object.values(edits).every((e) => e.display) ? 'Uncheck All' : 'Check All'}
                                    </Button>
                                </th>
                                <th style={{ textAlign: 'center' }}>Display Qty</th>
                                <th style={{ textAlign: 'center' }}>Display Unit Price</th>
                            </tr>
                        </thead>
                        <tbody>
                            {table_info.map((order, orderIndex) => {
                                const orderRowSpanLength = order.items.reduce((acc, item) => {
                                    return acc + item.specifications.reduce((specAcc, spec) => specAcc + spec.gsms.length, 0);
                                }, 0);

                                const orderRows = order.items.map((item, itemIndex) => {
                                    const itemRowSpanLength = item.specifications.reduce((acc, spec) => acc + spec.gsms.length, 0);

                                    return item.specifications.map((spec, specIndex) =>
                                        spec.gsms.map((gsm, gsmIndex) => {
                                            const edit = edits[gsm.orderGsmId] || { display: false, qty: '', unitPrice: '' };
                                            return (
                                                <tr key={`${orderIndex}-${itemIndex}-${specIndex}-${gsmIndex}`}>
                                                    {itemIndex === 0 && specIndex === 0 && gsmIndex === 0 && (
                                                        <>
                                                            <td rowSpan={orderRowSpanLength} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                                {order.style}
                                                            </td>
                                                            <td rowSpan={orderRowSpanLength} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                                {order.buyer_name}
                                                            </td>
                                                        </>
                                                    )}
                                                    {specIndex === 0 && gsmIndex === 0 && (
                                                        <td rowSpan={itemRowSpanLength} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                            {item.item_name}
                                                        </td>
                                                    )}
                                                    <td style={{ textAlign: 'center' }}>{spec.specification}</td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {gsm.measurement.split('\n').map((line, lineIndex) => (
                                                            <React.Fragment key={lineIndex}>
                                                                {line}
                                                                <br />
                                                            </React.Fragment>
                                                        ))}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>{gsm.orderQuantity}/Pcs</td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={!!edit.display}
                                                            onChange={() => handle_checkbox_change(gsm.orderGsmId)}
                                                        />
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <Form.Control
                                                            type="number"
                                                            size="sm"
                                                            style={{ width: '90px', margin: '0 auto' }}
                                                            value={edit.qty}
                                                            onChange={(e) => handle_field_change(gsm.orderGsmId, 'qty', e.target.value)}
                                                        />
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <Form.Control
                                                            type="number"
                                                            size="sm"
                                                            style={{ width: '100px', margin: '0 auto' }}
                                                            value={edit.unitPrice}
                                                            title={
                                                                edit.exactUnitPrice !== '' && String(edit.exactUnitPrice) !== String(edit.unitPrice)
                                                                    ? `Exact stored value: ${edit.exactUnitPrice}`
                                                                    : undefined
                                                            }
                                                            onChange={(e) => handle_field_change(gsm.orderGsmId, 'unitPrice', e.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    );
                                });

                                return <React.Fragment key={orderIndex}>{orderRows}</React.Fragment>;
                            })}
                        </tbody>
                    </Table>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
                <Button variant="primary" onClick={handle_save} disabled={loading || saving}>
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditDisplayModal;